/**
 * A task's dates on one calendar, the way a hotel or airline booking picks them (Duncan 2026-10-25: "when you set your
 * start date, then you click your end date it shows you how many days you have selected so far. It's just a good way
 * to visually see what you're scheduling"; the mockup he OK'd). From Start: click the start, then the finish. From
 * Finish: the start is shown and you click the finish. The days between are shaded and counted the way Summit counts a
 * duration (server/scheduleEngine.js): work days Mon–Fri with both ends included, or every day on a job with a 7-day
 * week. On a Mon–Fri job weekends show but can't be picked (his pick). The job's own Start / End count every day
 * (workWeek '7', endWord 'end'). It floats over the page at its field (a portal), so no card or window that clips
 * its content can cut it off, and it opens upward when there's no room below.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const DAY = 86400000;
const toN = (iso) => (iso ? Math.floor(Date.parse(`${String(iso).slice(0, 10)}T00:00:00Z`) / DAY) : null);
export const toIso = (n) => new Date(n * DAY).toISOString().slice(0, 10);
const dowOf = (n) => new Date(n * DAY).getUTCDay();
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const short = (n) => new Date(n * DAY).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });

/** Days from a to b, both included: work days Mon–Fri, or every day on a 7-day week (scheduleEngine's span). */
export function workSpan(a, b, workWeek) {
  const x = typeof a === 'string' ? toN(a) : a;
  const y = typeof b === 'string' ? toN(b) : b;
  if (x == null || y == null || y < x) return 0;
  if (String(workWeek) === '7') return y - x + 1;
  let count = 0;
  for (let d = x; d <= y; d += 1) { const w = dowOf(d); if (w !== 0 && w !== 6) count += 1; }
  return count;
}

const Chevron = ({ left }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={left ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} />
  </svg>
);

export default function DateRangePicker({
  start, finish, workWeek = '5', mode: openedFor, onStart, onFinish, onClose, anchorRef, align = 'right', endWord = 'finish', footer = null,
  countBetween = false, // the job's Start / End: the days between them, as the Overview's Timeline label counts (hotel nights)
}) {
  const seven = String(workWeek) === '7';
  const [mode, setMode] = useState(openedFor); // 'start' | 'finish'
  const [from, setFrom] = useState(() => toN(start));
  const [hover, setHover] = useState(null);
  const today = Math.floor(Date.now() / DAY);
  const [month, setMonth] = useState(() => {
    const n = toN(openedFor === 'finish' ? finish || start : start || finish) ?? today;
    const d = new Date(n * DAY);
    return { y: d.getUTCFullYear(), m: d.getUTCMonth() };
  });
  const ref = useRef(null);
  const [pos, setPos] = useState(null); // { top, left } on the screen

  // Under its field (or above it when the screen ends first), kept there as the page or a window scrolls.
  useLayoutEffect(() => {
    const place = () => {
      const a = anchorRef && anchorRef.current;
      if (!a) return;
      const r = a.getBoundingClientRect();
      const w = Math.min(300, window.innerWidth - 16);
      const h = ref.current ? ref.current.offsetHeight : 360;
      const left = Math.max(8, Math.min(align === 'left' ? r.left : r.right - w, window.innerWidth - w - 8));
      let top = r.bottom + 6;
      if (top + h > window.innerHeight - 8 && r.top - 6 - h >= 8) top = r.top - 6 - h;
      setPos({ top, left, w });
    };
    place();
    // A field that appears together with the calendar (an inline field switching to its input) isn't there yet:
    // measure again once it is.
    const frame = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchorRef, align, month, mode]);

  // A click outside (not on the field it opened from) or Escape closes it; Escape doesn't also close the task window.
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (anchorRef && anchorRef.current && anchorRef.current.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true); };
  }, [onClose, anchorRef]);

  const to = mode === 'finish' ? (hover ?? (toN(finish) != null && from != null && toN(finish) >= from ? toN(finish) : null)) : null;
  const count = from != null && to != null ? (countBetween ? to - from : workSpan(from, to, workWeek)) : 0;
  const pickable = (n) => (seven || (dowOf(n) !== 0 && dowOf(n) !== 6)) && !(mode === 'finish' && from != null && n < from);
  const pick = (n) => {
    if (!pickable(n)) return;
    if (mode === 'start') {
      setFrom(n);
      setMode('finish');
      setHover(null);
      onStart(toIso(n));
      return;
    }
    onFinish(toIso(n), workSpan(from, n, workWeek));
    onClose();
  };

  const first = Math.floor(Date.UTC(month.y, month.m, 1) / DAY);
  const days = new Date(Date.UTC(month.y, month.m + 1, 0)).getUTCDate();
  const cells = [...Array(dowOf(first)).fill(null), ...Array.from({ length: days }, (_, i) => first + i)];
  const shift = (k) => setMonth(({ y, m }) => { const d = new Date(Date.UTC(y, m + k, 1)); return { y: d.getUTCFullYear(), m: d.getUTCMonth() }; });
  const unit = seven ? 'day' : 'work day';

  return createPortal(
    // mousedown here doesn't take focus from the field it opened from (an inline field commits and closes on blur)
    <div ref={ref} role="dialog" aria-label={mode === 'start' ? 'Pick the start' : `Pick the ${endWord}`} onMouseDown={(e) => e.preventDefault()}
      style={{ position: 'fixed', zIndex: 10060, top: pos ? pos.top : -9999, left: pos ? pos.left : -9999, width: pos ? pos.w : 300, boxSizing: 'border-box',
        background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-md)', padding: 12,
        fontFamily: 'var(--font-sans)', color: 'var(--text-1)' }}
      onMouseLeave={() => setHover(null)}>
      <div style={{ fontSize: 13.5, color: 'var(--text-1)', marginBottom: 8, minHeight: 20, display: 'flex', flexWrap: 'wrap', gap: '0 6px', alignItems: 'baseline' }}>
        {from == null || mode === 'start'
          ? <span style={{ fontWeight: 600 }}>Pick the start</span>
          : to == null
            ? <><span style={{ fontWeight: 600 }}>{short(from)} →</span><span style={{ color: 'var(--text-3)' }}>now pick the {endWord}</span></>
            : <><span style={{ fontWeight: 600 }}>{short(from)} → {short(to)}</span><span style={{ color: 'var(--accent-text)', fontWeight: 600 }}>· {count} {unit}{count === 1 ? '' : 's'}</span></>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <button type="button" onClick={() => shift(-1)} aria-label="The month before"
          style={{ border: 'none', background: 'transparent', color: 'var(--text-3)', cursor: 'pointer', padding: 4, display: 'inline-flex', borderRadius: 6 }}><Chevron left /></button>
        <span style={{ flex: 1, textAlign: 'center', fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>{MONTHS[month.m]} {month.y}</span>
        <button type="button" onClick={() => shift(1)} aria-label="The month after"
          style={{ border: 'none', background: 'transparent', color: 'var(--text-3)', cursor: 'pointer', padding: 4, display: 'inline-flex', borderRadius: 6 }}><Chevron /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', rowGap: 2, textAlign: 'center' }}>
        {WEEKDAYS.map((w) => <div key={w} style={{ fontSize: 11, color: 'var(--text-3)', padding: '2px 0' }}>{w}</div>)}
        {cells.map((n, i) => {
          if (n == null) return <div key={`b${i}`} />;
          const ok = pickable(n);
          const off = !seven && (dowOf(n) === 0 || dowOf(n) === 6);
          const isFrom = from != null && n === from;
          const isTo = to != null && n === to;
          const inside = from != null && to != null && n > from && n < to;
          const ends = isFrom || isTo;
          const radius = isFrom && isTo ? 6 : isFrom ? '6px 0 0 6px' : isTo ? '0 6px 6px 0' : inside ? 0 : 6;
          return (
            <button key={n} type="button" disabled={!ok} onClick={() => pick(n)} onMouseEnter={() => (mode === 'finish' && ok ? setHover(n) : null)}
              aria-label={`${short(n)}${off ? ', not a work day' : ''}`} aria-pressed={ends}
              style={{
                border: 'none', font: 'inherit', fontSize: 13, padding: '6px 0', borderRadius: radius, cursor: ok ? 'pointer' : 'default',
                fontVariantNumeric: 'tabular-nums',
                background: ends ? 'var(--accent)' : inside ? 'var(--accent-soft)' : 'transparent',
                color: ends ? '#fff' : off ? 'var(--text-4)' : !ok ? 'var(--text-4)' : inside ? 'var(--accent-text)' : 'var(--text-1)',
                fontWeight: ends || n === today ? 650 : 400,
                textDecoration: off ? 'line-through' : 'none',
                boxShadow: n === today && !ends ? 'inset 0 0 0 1px var(--border-strong)' : 'none',
              }}>
              {new Date(n * DAY).getUTCDate()}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 8 }}>
        {mode === 'start' ? `Click the start, then the ${endWord}. ` : `Hover a day to see the count; click it to set the ${endWord}. `}
        {footer || (seven ? 'Every day is a work day on this job.' : 'Weekends aren’t work days on this job.')}
      </div>
    </div>,
    document.body,
  );
}
